import { useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  useCreateProgressInstructionMutation,
  useListProgressRecipientsQuery,
  useLazyDownloadInstructionDocxQuery,
} from '@shared/api';
import {
  apiErrorMessage,
  instructionTopicTitle,
  saveBlob,
  type CreatedInstruction,
  type InstructionTopic,
} from '@entities/progress';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { Combobox } from '@shared/ui/Combobox';
import { Field, Input, Label } from '@shared/ui/Input';

/* «Поручение по показателю»: исполнитель, срок, текст (подсказка — по цифрам
   блока). Сервер сам снимает показатель с живого дашборда, формирует DOCX,
   кладёт уведомление в ОСНОВУ и отправляет сообщение в VK WorkSpace. */

const TextArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  resize: vertical;
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Hint = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Done = styled.div`
  font-size: 14px;
  line-height: 1.6;

  & ul {
    margin: ${({ theme }) => `${theme.spacing(2)} 0 ${theme.spacing(4)}`};
    padding-left: ${({ theme }) => theme.spacing(5)};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
`;

interface Props {
  projectId: string;
  objectName: string;
  topic: InstructionTopic;
  suggestedText: string;
  onClose: () => void;
}

export function InstructionModal({ projectId, objectName, topic, suggestedText, onClose }: Props) {
  const recipientsQ = useListProgressRecipientsQuery();
  const [create, { isLoading: creating }] = useCreateProgressInstructionMutation();
  const [download, { isFetching: downloading }] = useLazyDownloadInstructionDocxQuery();
  const [recipient, setRecipient] = useState('');
  const [due, setDue] = useState('');
  const [text, setText] = useState(suggestedText);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedInstruction | null>(null);

  const options = useMemo(
    () =>
      (recipientsQ.data ?? []).map((r) => ({
        id: r.key,
        label: r.name,
        sublabel: [
          r.position,
          r.department,
          r.has_vk ? 'VK' : null,
          r.has_account ? null : 'без учётки',
        ]
          .filter(Boolean)
          .join(' · '),
      })),
    [recipientsQ.data],
  );
  const chosen = recipientsQ.data?.find((r) => r.key === recipient);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!recipient) {
      setError('Выберите исполнителя.');
      return;
    }
    try {
      const res = await create({
        project_id: projectId,
        topic,
        text,
        recipient,
        due_date: due || null,
      }).unwrap();
      setCreated(res);
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось выдать поручение. Попробуйте ещё раз.');
    }
  }

  async function handleDownload() {
    if (!created) return;
    try {
      const blob = await download(created.id).unwrap();
      saveBlob(blob, `Поручение_${created.number}.docx`);
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось скачать документ.');
    }
  }

  return (
    <Modal open title={`Поручение · ${instructionTopicTitle(topic)}`} onClose={onClose}>
      {created ? (
        <Done>
          Поручение <b>{created.number}</b> по объекту «{objectName}» выдано:{' '}
          {created.assignee_name}.
          <ul>
            <li>
              {created.delivery.bell
                ? 'Уведомление в ОСНОВЕ отправлено.'
                : 'У исполнителя нет учётки в ОСНОВЕ — уведомления в ОСНОВЕ не будет.'}
            </li>
            <li>
              {created.delivery.vk
                ? 'Сообщение в VK WorkSpace отправлено.'
                : 'В VK WorkSpace не отправлено: нет адреса исполнителя или бот выключен.'}
            </li>
          </ul>
          <Actions>
            <Button
              type="button"
              $variant="secondary"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? 'Готовлю…' : 'Скачать документ (DOCX)'}
            </Button>
            <Button type="button" $variant="primary" onClick={onClose}>
              Готово
            </Button>
          </Actions>
          {error && <Err role="alert">{error}</Err>}
        </Done>
      ) : (
        <form onSubmit={handleSubmit}>
          <Field>
            <Label htmlFor="instr-recipient">Исполнитель</Label>
            <Combobox
              id="instr-recipient"
              value={recipient}
              selectedLabel={chosen?.name}
              options={options}
              onChange={(o) => setRecipient(o?.id ?? '')}
              placeholder="Начните вводить фамилию"
              loading={recipientsQ.isLoading}
              emptyText="Никого не нашлось"
              maxItems={50}
            />
            <Hint>
              Сотрудники с учёткой получат уведомление в ОСНОВЕ, с адресом VK — сообщение с кнопкой
              «Выполнено».
            </Hint>
          </Field>
          <Field>
            <Label htmlFor="instr-due">Срок исполнения</Label>
            <Input
              id="instr-due"
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </Field>
          <Field>
            <Label htmlFor="instr-text">Что поручается</Label>
            <TextArea
              id="instr-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={4000}
            />
            <Hint>
              Цифры показателя на сегодня сервер добавит в документ сам — их не нужно переписывать.
            </Hint>
          </Field>
          {error && <Err role="alert">{error}</Err>}
          <Actions>
            <Button type="button" $variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" $variant="primary" disabled={creating}>
              {creating ? 'Выдаю…' : 'Выдать поручение'}
            </Button>
          </Actions>
        </form>
      )}
    </Modal>
  );
}
